import type { LocalizedText } from "../schema/common.js";

/**
 * Alliance research (82-alliance.md R88, D104): three trees of six technologies with five levels each,
 * funded by donation lots like the buildings. Their effects are the ones the buildings do not have —
 * production, construction, repair, storage, research, scrip; cargo, marches, help, scouting,
 * territory, march speed; the fight against the infected, raids, arena, duel points.
 */

export type AllianceResearchTree = "development" | "territory" | "battle";
export const ALLIANCE_RESEARCH_TREES: readonly AllianceResearchTree[] = ["development", "territory", "battle"];

/** What a level of a technology adds for every member; the bonus pipelines read these keys. */
export type AllianceResearchEffectKey =
  | "productionPct" | "buildSpeedPct" | "repairSlots" | "storagePct" | "researchSpeedPct" | "scripPct"
  | "cargoPct" | "marchSlots" | "assistMax" | "sensorLevel" | "territoryRadius" | "marchSpeedPct"
  | "infectedAtkPct" | "infectedDefPct" | "raidAttempts" | "infectedHpPct" | "arenaAttempts" | "duelPointsPct";

export interface AllianceTechDef {
  id: string;
  tree: AllianceResearchTree;
  /** Row on the board: 1 at the top, 3 at the bottom; two technologies per row. */
  tier: 1 | 2 | 3;
  name: LocalizedText;
  /** Technologies that must have level 1 first. */
  requires: readonly string[];
  /** The alliance level the technology needs, and the building level where content says so. */
  needs: { allianceLevel: number; building?: { id: string; level: number } };
  effect: { key: AllianceResearchEffectKey; perLevel: number; whole?: boolean };
  /** Progress points for each level, 1 to 5. */
  progress: readonly [number, number, number, number, number];
}

export const ALLIANCE_TECH_MAX_LEVEL = 5;

const STEPS: Record<1 | 2 | 3, readonly [number, number, number, number, number]> = {
  1: [300, 500, 800, 1_200, 1_800],
  2: [500, 800, 1_200, 1_800, 2_600],
  3: [800, 1_200, 1_800, 2_600, 3_600],
};

function tech(id: string, tree: AllianceResearchTree, tier: 1 | 2 | 3, de: string, en: string, requires: readonly string[], needs: AllianceTechDef["needs"], effect: AllianceTechDef["effect"]): AllianceTechDef {
  return { id, tree, tier, name: { de, en }, requires, needs, effect, progress: STEPS[tier] };
}

export const ALLIANCE_RESEARCH: readonly AllianceTechDef[] = [
  // Development: what the colonies produce and build.
  tech("ar.supply_lines", "development", 1, "Versorgungslinien", "Supply Lines", [], { allianceLevel: 2 }, { key: "productionPct", perLevel: 2 }),
  tech("ar.construction_corps", "development", 1, "Baukorps", "Construction Corps", [], { allianceLevel: 2 }, { key: "buildSpeedPct", perLevel: 2 }),
  tech("ar.field_workshops", "development", 2, "Feldwerkstätten", "Field Workshops", ["ar.construction_corps"], { allianceLevel: 4 }, { key: "repairSlots", perLevel: 0.4, whole: true }),
  tech("ar.deep_storage", "development", 2, "Tiefenlager", "Deep Storage", ["ar.supply_lines"], { allianceLevel: 4 }, { key: "storagePct", perLevel: 3 }),
  tech("ar.research_grants", "development", 3, "Forschungsstipendien", "Research Grants", ["ar.deep_storage"], { allianceLevel: 6, building: { id: "ab.muster_hall", level: 2 } }, { key: "researchSpeedPct", perLevel: 2 }),
  tech("ar.salvage_yards", "development", 3, "Schrottplätze", "Salvage Yards", ["ar.field_workshops"], { allianceLevel: 6 }, { key: "scripPct", perLevel: 3 }),
  // Territory: how far and how much the members carry.
  tech("ar.cargo_rigs", "territory", 1, "Lastenrigs", "Cargo Rigs", [], { allianceLevel: 2 }, { key: "cargoPct", perLevel: 4 }),
  tech("ar.trail_marking", "territory", 1, "Wegmarkierung", "Trail Marking", [], { allianceLevel: 2 }, { key: "marchSpeedPct", perLevel: 2 }),
  tech("ar.mutual_aid", "territory", 2, "Nachbarschaftshilfe", "Mutual Aid", ["ar.cargo_rigs"], { allianceLevel: 4, building: { id: "ab.embassy", level: 1 } }, { key: "assistMax", perLevel: 1 }),
  tech("ar.long_range_optics", "territory", 2, "Fernoptik", "Long-Range Optics", ["ar.trail_marking"], { allianceLevel: 4 }, { key: "sensorLevel", perLevel: 1 }),
  tech("ar.border_posts", "territory", 3, "Grenzposten", "Border Posts", ["ar.mutual_aid"], { allianceLevel: 6, building: { id: "ab.logistics", level: 2 } }, { key: "territoryRadius", perLevel: 1 }),
  tech("ar.convoy_doctrine", "territory", 3, "Konvoi-Doktrin", "Convoy Doctrine", ["ar.long_range_optics"], { allianceLevel: 6 }, { key: "marchSlots", perLevel: 0.2, whole: true }),
  // Battle: the fight against the infected and the alliance's own contests.
  tech("ar.anti_infected_rounds", "battle", 1, "Anti-Infizierten-Munition", "Anti-Infected Rounds", [], { allianceLevel: 2 }, { key: "infectedAtkPct", perLevel: 3 }),
  tech("ar.bite_plating", "battle", 1, "Bissplatten", "Bite Plating", [], { allianceLevel: 2 }, { key: "infectedDefPct", perLevel: 3 }),
  tech("ar.field_medics", "battle", 2, "Feldsanitäter", "Field Medics", ["ar.bite_plating"], { allianceLevel: 4 }, { key: "infectedHpPct", perLevel: 2 }),
  tech("ar.raid_logistics", "battle", 2, "Raid-Logistik", "Raid Logistics", ["ar.anti_infected_rounds"], { allianceLevel: 4, building: { id: "ab.war_academy", level: 2 } }, { key: "raidAttempts", perLevel: 0.4, whole: true }),
  tech("ar.arena_scouting", "battle", 3, "Arena-Späher", "Arena Scouts", ["ar.field_medics"], { allianceLevel: 6 }, { key: "arenaAttempts", perLevel: 0.2, whole: true }),
  tech("ar.war_bonds", "battle", 3, "Kriegsanleihen", "War Bonds", ["ar.raid_logistics"], { allianceLevel: 6 }, { key: "duelPointsPct", perLevel: 2 }),
];

const techMap = new Map(ALLIANCE_RESEARCH.map((t) => [t.id, t]));
export function allianceTechById(id: string): AllianceTechDef {
  const found = techMap.get(id);
  if (!found) throw new Error(`unknown alliance technology ${id}`);
  return found;
}

/** The bonus a technology gives at a level; "whole" effects count only full steps (a slot cannot be 0.4 of a slot). */
export function allianceTechBonus(def: AllianceTechDef, level: number): number {
  const raw = def.effect.perLevel * Math.max(0, level);
  return def.effect.whole ? Math.floor(raw + 1e-9) : Math.round(raw * 100) / 100;
}

export const ALLIANCE_RESEARCH_TREE_NAMES: Record<AllianceResearchTree, LocalizedText> = {
  development: { de: "Entwicklung", en: "Development" },
  territory: { de: "Territorium", en: "Territory" },
  battle: { de: "Kampf", en: "Battle" },
};
